import {FC} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {CheckCircle} from '@mui/icons-material';
import {RequestHistoryItem} from '../../types/eppiCostume';

interface RequestHistoryCardProps {
    req: RequestHistoryItem;
    selectedRequestId: number | null;
    selectedResultId: number | null;
    onSelect: (requestId: number, resultId: number) => void;
    onFinalize: () => void;
    isFinalizing: boolean;
}

const ACTIVE_STATUSES = new Set(['PENDING', 'RUNNING', 'AWAITING_RESULTS']);

function statusChipColor(
    status: RequestHistoryItem['status']
): 'success' | 'error' | 'warning' | 'default' {
    if (status === 'SUCCEEDED') return 'success';
    if (status === 'FAILED') return 'error';
    if (status === 'CANCELED') return 'default';
    return 'warning'; // PENDING / RUNNING / AWAITING_RESULTS
}

// userContext 에서 첫 줄의 [Costume Code: ...] prefix 제거하고 Theme 부분 추출
function extractTheme(userContext: string): string {
    if (!userContext) return '';
    const lines = userContext.split('\n');
    let idx = 0;
    while (idx < lines.length && lines[idx].startsWith('[')) idx += 1;
    while (idx < lines.length && lines[idx].trim() === '') idx += 1;
    return lines.slice(idx).join('\n').trim();
}

function formatCreatedAt(iso: string): string {
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    } catch {
        return iso;
    }
}

export const RequestHistoryCard: FC<RequestHistoryCardProps> = ({
    req,
    selectedRequestId,
    selectedResultId,
    onSelect,
    onFinalize,
    isFinalizing,
}) => {
    const isActive = ACTIVE_STATUSES.has(req.status);
    const theme = extractTheme(req.userContext);
    const hasResults = req.results.length > 0;
    const cardSelected = selectedRequestId === req.requestId;

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderColor: cardSelected ? 'primary.main' : 'divider',
                borderWidth: cardSelected ? 2 : 1,
            }}
        >
            <Stack spacing={1.5}>
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                >
                    <Typography variant="subtitle2" fontWeight={700}>
                        Request #{req.requestId}
                    </Typography>
                    <Chip
                        size="small"
                        label={req.status}
                        color={statusChipColor(req.status)}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {req.provider} · {req.mode} · {formatCreatedAt(req.createdAt)}
                        {req.baseImageUrl ? ' · base ✓' : ''}
                    </Typography>
                    {isActive && <CircularProgress size={14} />}
                </Stack>

                {/* Theme */}
                {theme && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {theme}
                    </Typography>
                )}

                {/* Error */}
                {req.status === 'FAILED' && req.errorMessage && (
                    <Alert severity="error">{req.errorMessage}</Alert>
                )}

                {/* 진행 중 */}
                {isActive && (
                    <Alert severity="info" icon={<CircularProgress size={16} />}>
                        진행 중 (5초마다 자동 새로고침)
                        {!hasResults && ' · 결과 아직 없음'}
                    </Alert>
                )}

                {/* Results grid */}
                {hasResults && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: 1.5,
                        }}
                    >
                        {req.results.map((r) => {
                            const selected =
                                cardSelected && r.resultId === selectedResultId;
                            return (
                                <Box
                                    key={r.resultId}
                                    onClick={() =>
                                        onSelect(req.requestId, r.resultId)
                                    }
                                    sx={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        border: '2px solid',
                                        borderColor: selected
                                            ? 'primary.main'
                                            : 'divider',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        bgcolor: 'grey.50',
                                        aspectRatio: '1 / 1',
                                        transition: 'border-color 120ms',
                                        '&:hover': {
                                            borderColor: 'primary.light',
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={r.imageUrl}
                                        alt={`result-${r.resultId}`}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                    {selected && (
                                        <CheckCircle
                                            sx={{
                                                position: 'absolute',
                                                top: 6,
                                                right: 6,
                                                color: 'primary.main',
                                                bgcolor: 'background.paper',
                                                borderRadius: '50%',
                                            }}
                                        />
                                    )}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            position: 'absolute',
                                            bottom: 4,
                                            left: 6,
                                            color: 'common.white',
                                            textShadow: '0 0 4px rgba(0,0,0,0.6)',
                                        }}
                                    >
                                        #{r.sequence}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* SUCCEEDED but empty */}
                {req.status === 'SUCCEEDED' && !hasResults && (
                    <Alert severity="warning">
                        성공 응답이지만 결과 이미지가 없습니다.
                    </Alert>
                )}

                {/* Finalize CTA — 이 카드에서 선택된 경우만 활성 */}
                {hasResults && (
                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={
                                !cardSelected ||
                                selectedResultId == null ||
                                isFinalizing ||
                                req.status !== 'SUCCEEDED'
                            }
                            onClick={onFinalize}
                        >
                            {isFinalizing
                                ? '확정 중...'
                                : '이 결과로 확정 (적용)'}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};
